import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Loader2, AlertCircle, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

// Load an external <script> exactly once, deduped across mounts.
const scriptPromises = new Map<string, Promise<void>>();
function loadScriptOnce(src: string): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Allow a retry on the next mount if the CDN was unreachable.
      scriptPromises.delete(src);
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
  scriptPromises.set(src, promise);
  return promise;
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSocialSubmitting, setIsSocialSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { login, socialLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID;
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Get the redirect path (with its query, e.g. /register?complete=1) from location state
  const fromLoc = (location.state as { from?: { pathname: string; search?: string } })?.from;
  const from = fromLoc?.pathname ? fromLoc.pathname + (fromLoc.search || '') : '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      trackEvent('login', { method: 'email' });
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Complete a social sign-in: exchange the provider id_token with the
  // backend, then navigate exactly like the password flow does. Backend
  // detail text (401 rejected / 403 unverified email / 501 not configured)
  // is thrown by socialLogin and lands in the page's error banner.
  const completeSocialLogin = async (
    provider: 'google' | 'apple',
    idToken: string,
    fullName?: string
  ) => {
    setError(null);
    setIsSocialSubmitting(true);
    try {
      await socialLogin(provider, idToken, fullName);
      trackEvent('login', { method: provider });
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Sign-in failed');
    } finally {
      setIsSocialSubmitting(false);
    }
  };

  // Keep the latest completer in a ref so the once-only Google init effect
  // never calls a stale closure (e.g. after `from` changes).
  const completeSocialLoginRef = useRef(completeSocialLogin);
  completeSocialLoginRef.current = completeSocialLogin;

  // GOOGLE — load the GSI script once, then render Google's own button.
  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;

    loadScriptOnce(GOOGLE_GSI_SRC)
      .then(() => {
        if (cancelled) return;
        const google = window.google;
        const container = googleButtonRef.current;
        if (!google || !container) return;
        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            void completeSocialLoginRef.current('google', response.credential);
          },
        });
        google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          width: container.offsetWidth || 320,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Google sign-in. Please try again later.');
      });

    return () => {
      cancelled = true;
    };
  }, [googleClientId]);

  // APPLE — load Apple's JS once and initialize the auth client.
  useEffect(() => {
    if (!appleClientId) return;
    let cancelled = false;

    loadScriptOnce(APPLE_JS_SRC)
      .then(() => {
        if (cancelled || !window.AppleID) return;
        window.AppleID.auth.init({
          clientId: appleClientId,
          scope: 'name email',
          redirectURI: window.location.origin + '/login',
          usePopup: true,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Apple sign-in. Please try again later.');
      });

    return () => {
      cancelled = true;
    };
  }, [appleClientId]);

  const handleAppleSignIn = async () => {
    const appleId = window.AppleID;
    if (!appleId) {
      setError('Apple sign-in is still loading. Please try again.');
      return;
    }

    let response: AppleSignInResponse;
    try {
      response = await appleId.auth.signIn();
    } catch (err) {
      // Apple rejects with { error: 'popup_closed_by_user' } when the user
      // simply closes the popup — that's a cancel, not an error.
      const appleError = (err as { error?: string })?.error;
      if (appleError !== 'popup_closed_by_user') {
        setError('Apple sign-in failed. Please try again.');
      }
      return;
    }

    // Apple only sends the user's name on the FIRST authorization.
    const name = response.user?.name;
    const fullName =
      [name?.firstName, name?.lastName].filter(Boolean).join(' ') || undefined;
    await completeSocialLogin('apple', response.authorization.id_token, fullName);
  };

  return (
    <div className="min-h-screen bg-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo className="h-12" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white">
          Welcome to SolanIQ
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Battery optimization platform
        </p>
      </div>

      <div className="mt-8 mx-auto w-full sm:max-w-md lg:max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* New customer panel */}
          <div className="bg-card py-8 px-4 shadow-card sm:rounded-2xl sm:px-10 flex flex-col">
            <h3 className="text-center text-xl font-bold text-gray-900 dark:text-white">
              New Customer?
            </h3>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Create your account in minutes and let SolanIQ take over your battery.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-brand-orange flex-shrink-0" />
                <span>Automatic battery optimization against live electricity prices</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-brand-orange flex-shrink-0" />
                <span>Live solar, battery and grid monitoring</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-brand-orange flex-shrink-0" />
                <span>$10/month inc GST — cancel anytime</span>
              </li>
            </ul>
            <div className="flex-1" />
            <Link
              to="/register"
              className="mt-8 w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-base font-medium text-white bg-brand-orange hover:bg-brand-orange-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors"
            >
              Register Here
            </Link>
          </div>

          {/* Sign in card */}
          <div className="bg-card py-8 px-4 shadow-card sm:rounded-2xl sm:px-10">
            <h3 className="text-center text-xl font-bold text-gray-900 dark:text-white mb-6">
              Sign in
            </h3>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-card dark:bg-brand-navy-dark text-gray-900 dark:text-white focus:outline-none focus:ring-brand-orange focus:border-brand-orange sm:text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-card dark:bg-brand-navy-dark text-gray-900 dark:text-white focus:outline-none focus:ring-brand-orange focus:border-brand-orange sm:text-sm pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          {(googleClientId || appleClientId) && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card dark:bg-brand-navy text-gray-500 dark:text-gray-400">
                    or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {googleClientId && (
                  <div ref={googleButtonRef} className="flex justify-center" />
                )}

                {appleClientId && (
                  <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={isSocialSubmitting}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-card dark:bg-brand-navy-dark hover:bg-gray-50 dark:hover:bg-brand-navy focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSocialSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <svg
                        className="w-4 h-4 fill-current"
                        viewBox="0 0 814 1000"
                        aria-hidden="true"
                      >
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
                      </svg>
                    )}
                    Sign in with Apple
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="mt-4 text-center text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-gray-600 dark:text-gray-400 hover:text-brand-orange"
            >
              Forgot password?
            </Link>
          </p>

          <p className="mt-2 text-center text-sm">
            <Link
              to="/contact"
              className="font-medium text-gray-600 dark:text-gray-400 hover:text-brand-orange"
            >
              Questions? Visit our help & FAQ
            </Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
}
